import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useMemo, useReducer } from "react";

import {
  buildSearchResultNavigationSnapshot,
  getSearchResultNavigationQuery,
  getSearchResultNavigationState,
  hasSearchResultNavigationContext,
} from "../util/searchResultNavigation";

const SEARCH_RESULT_NAVIGATION_ACTIONS = {
  NAVIGATION_FETCH_FINISHED: "NAVIGATION_FETCH_FINISHED",
  NAVIGATION_FETCH_STARTED: "NAVIGATION_FETCH_STARTED",
  SNAPSHOT_UPDATED: "SNAPSHOT_UPDATED",
};

const buildInitialSearchResultNavigationState = navigationSnapshot => ({
  navigationLoading: false,
  navigationSnapshot,
});

const searchResultNavigationReducer = (state, action) => {
  switch (action.type) {
    case SEARCH_RESULT_NAVIGATION_ACTIONS.NAVIGATION_FETCH_STARTED:
      return { ...state, navigationLoading: true };
    case SEARCH_RESULT_NAVIGATION_ACTIONS.NAVIGATION_FETCH_FINISHED:
      return { ...state, navigationLoading: false };
    case SEARCH_RESULT_NAVIGATION_ACTIONS.SNAPSHOT_UPDATED:
      return { ...state, navigationSnapshot: action.payload };
    default:
      return state;
  }
};

const isTypingTarget = target => {
  if (!(target instanceof HTMLElement)) return false;

  const tagName = target?.tagName?.toLowerCase();
  return (
    target.isContentEditable ||
    ["input", "textarea", "select"].includes(tagName) ||
    target.closest("[contenteditable='true']")
  );
};

const useSearchResultNavigation = ({
  activeSubPage = "details",
  buildTargetUrl,
  dataKey,
  fetchQuery,
  listContext = {},
  navigate,
  navigationStore,
  recordId,
  status = "all",
}) => {
  const apolloClient = useApolloClient();
  const [{ navigationLoading, navigationSnapshot }, dispatch] = useReducer(
    searchResultNavigationReducer,
    navigationStore.get(),
    buildInitialSearchResultNavigationState,
  );

  useEffect(() => {
    const unsubscribe = navigationStore.subscribe(nextSnapshot => {
      dispatch({
        payload: nextSnapshot,
        type: SEARCH_RESULT_NAVIGATION_ACTIONS.SNAPSHOT_UPDATED,
      });
    });
    return () => unsubscribe();
  }, [navigationStore]);

  const getTargetUrl = useCallback(
    targetId =>
      buildTargetUrl({
        activeSubPage,
        listContext: navigationSnapshot?.listContext || listContext,
        recordId: targetId,
        status: navigationSnapshot?.statusContext || status,
      }),
    [activeSubPage, buildTargetUrl, listContext, navigationSnapshot, status],
  );

  const navigationState = useMemo(
    () =>
      getSearchResultNavigationState({
        recordId,
        snapshot: navigationSnapshot,
      }),
    [recordId, navigationSnapshot],
  );
  const hasNavigationContext = useMemo(
    () =>
      hasSearchResultNavigationContext({
        snapshot: navigationSnapshot,
        status,
      }),
    [navigationSnapshot, status],
  );

  const fetchNavigationPage = useCallback(
    async ({ pageOffset }) => {
      const currentSnapshot = navigationStore.get();
      const queryInput = getSearchResultNavigationQuery(
        navigationStore,
        pageOffset,
      );

      dispatch({
        type: SEARCH_RESULT_NAVIGATION_ACTIONS.NAVIGATION_FETCH_STARTED,
      });
      try {
        const { data } = await apolloClient.query({
          query: fetchQuery,
          variables: { input: queryInput },
          fetchPolicy: "network-only",
        });
        const result = data?.[dataKey];
        if (navigationStore.get() !== currentSnapshot) return;
        const nextSnapshot = buildSearchResultNavigationSnapshot({
          count: result?.count || 0,
          edges: result?.edges || [],
          listContext: currentSnapshot?.listContext || {},
          queryVariables: queryInput,
          filters: navigationStore.filterStore.get(),
          searchSynopsisTerms: currentSnapshot?.searchSynopsisTerms,
          statusContext: currentSnapshot?.statusContext || status,
        });
        navigationStore.set(nextSnapshot);
        return nextSnapshot;
      } finally {
        dispatch({
          type: SEARCH_RESULT_NAVIGATION_ACTIONS.NAVIGATION_FETCH_FINISHED,
        });
      }
    },
    [apolloClient, dataKey, fetchQuery, navigationStore, status],
  );

  const navigateToPrevious = useCallback(async () => {
    if (
      !recordId ||
      !hasNavigationContext ||
      navigationLoading ||
      !navigationState?.hasPrevious
    ) {
      return;
    }

    if (navigationState?.previousId) {
      navigate(getTargetUrl(navigationState.previousId));
      return;
    }
    if (navigationState?.previousOffset === null) return;

    const nextSnapshot = await fetchNavigationPage({
      pageOffset: navigationState.previousOffset,
    });
    const previousPageIds = nextSnapshot?.pageIds || [];
    const previousPageLastId = previousPageIds[previousPageIds.length - 1];
    if (previousPageLastId) {
      navigate(getTargetUrl(previousPageLastId));
    }
  }, [
    fetchNavigationPage,
    getTargetUrl,
    hasNavigationContext,
    navigate,
    navigationLoading,
    navigationState,
    recordId,
  ]);

  const navigateToNext = useCallback(async () => {
    if (
      !recordId ||
      !hasNavigationContext ||
      navigationLoading ||
      !navigationState?.hasNext
    ) {
      return;
    }

    if (navigationState?.nextId) {
      navigate(getTargetUrl(navigationState.nextId));
      return;
    }
    if (navigationState?.nextOffset === null) return;

    const nextSnapshot = await fetchNavigationPage({
      pageOffset: navigationState.nextOffset,
    });
    const nextPageFirstId = nextSnapshot?.pageIds?.[0];
    if (nextPageFirstId) {
      navigate(getTargetUrl(nextPageFirstId));
    }
  }, [
    fetchNavigationPage,
    getTargetUrl,
    hasNavigationContext,
    navigate,
    navigationLoading,
    navigationState,
    recordId,
  ]);

  const showNavigationControls =
    typeof recordId === "number" && Number.isFinite(recordId);
  const hasPrevious =
    hasNavigationContext && Boolean(navigationState?.hasPrevious);
  const hasNext = hasNavigationContext && Boolean(navigationState?.hasNext);

  useEffect(() => {
    if (!showNavigationControls) return;

    const onKeyDown = event => {
      if (event.defaultPrevented || event.repeat) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
        return;
      if (isTypingTarget(event.target)) return;

      if (event.key === "ArrowLeft" && hasPrevious) {
        event.preventDefault();
        navigateToPrevious();
      }
      if (event.key === "ArrowRight" && hasNext) {
        event.preventDefault();
        navigateToNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    hasNext,
    hasPrevious,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  ]);

  return {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigationSnapshot,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  };
};

export {
  buildInitialSearchResultNavigationState,
  searchResultNavigationReducer,
  SEARCH_RESULT_NAVIGATION_ACTIONS,
};

export default useSearchResultNavigation;
